import React, { useMemo, useState } from 'react';
import Loader from '../../common/Loader';
import PageTitle from '../../components/PageTitle';
import { Box, Card, CardContent, Paper, Typography } from '@mui/material';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { FormProvider, RHFSelectField, RHFTextField } from '../../common/hook-form';
import { useForm } from 'react-hook-form';
import { apiConstants } from '../../common/constants';
import { formatDate, getMerchantName } from '../../helpers/UtilityHelper';
import { useGenericPostRequestQuery, useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls';
import WhatsAppChatPopup from './Conversation';
import { MerchantFilterCached } from '../../common/ProshipFormComponents';
import { FieldLabel, FilterField } from '../../common/FilterBar';
import { kpiCardSx, kpiRowSx, filterButtonSx, accentColor } from '../../common/sharedStyles';
import palette from '../../../theme/palette';
import CopyIcon from '../../common/CopyIcon';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold }) => (
    <Box sx={{ fontSize: 12.5, lineHeight: 1.4, color: muted ? 'text.secondary' : 'text.primary', fontWeight: bold ? 600 : 400 }}>
        {children}
    </Box>
);

// ─── KPI Row ──────────────────────────────────────────────────────────────────

function WAKPIRow({ data }) {
    const kpis = useMemo(() => {
        const rows = data?.data;
        if (!rows || !rows.length) return null;
        const total = rows.length;
        const completed = rows.filter(r => r.isconversationCompleted === true).length;
        const completedPct = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;
        const msgCounts = rows.map(r => r.conversation_history?.length || 0);
        const avgMsg = msgCounts.length ? (msgCounts.reduce((a, b) => a + b, 0) / msgCounts.length).toFixed(1) : 0;
        const uniqueMerchants = new Set(rows.map(r => r.meta_data?.merchantOid).filter(Boolean)).size;
        return { total, completed, completedPct, avgMsg, uniqueMerchants };
    }, [data]);
    if (!kpis) return null;

    const cards = [
        { label: 'Total Messages Sent', value: kpis.total, color: 'primary' },
        { label: 'Conversations Completed', value: `${kpis.completed} (${kpis.completedPct}%)`, color: 'success' },
        { label: 'Avg Messages / Convo', value: kpis.avgMsg, color: 'info' },
        { label: 'Unique Merchants', value: kpis.uniqueMerchants, color: 'warning' },
    ];

    return (
        <Box sx={kpiRowSx}>
            {cards.map(c => (
                <Paper key={c.label} sx={kpiCardSx(c.color)}>
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem' }}>
                        {c.label}
                    </Typography>
                    <Typography variant='h5' sx={{ mt: 0.5, fontWeight: 800, color: accentColor(c.color) }}>
                        {c.value}
                    </Typography>
                </Paper>
            ))}
        </Box>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────

const WhatsAppNDR = () => {
    const methods = useForm({
        defaultValues: { awb: "", merchant: '', isconversationCompleted: '' }
    });
    const { handleSubmit, reset } = methods;

    const [page, setPage] = useState(0);
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery();
    const [sizePerPage, setSizePerPage] = useState(20);
    const [conversation, setConversation] = React.useState(null);
    const [filterChange, setFiltersChange] = useState(0);
    const [filters, setFilters] = useState(0);

    const request = useMemo(() => {
        const andfilter = [];
        andfilter.push({ "field": "template_name", operator: "in", value: ['ndr_workflow_initiate', 'ndr_workflow_initiate_without_address_flow'] });
        andfilter.push({ "field": "status", operator: "eq", value: 'success' });
        if (filters.awb) andfilter.push({ "field": "meta_data.awbNumber", "operator": "eq", "value": filters.awb });
        if (filters.merchant) andfilter.push({ "field": "meta_data.merchantOid", "operator": "eq", "value": filters.merchant });
        if (filters.isconversationCompleted) andfilter.push({ "field": "isconversationCompleted", "operator": "eq", "value": filters.isconversationCompleted });
        return {
            "andfilter": andfilter,
            "offset": (page) * sizePerPage, "limit": sizePerPage, "skipCount": true,
            collection: "interaction_audit",
            excludes: "payload_body,status_history,wamid,endpoint,template_type,template_name,identifier,associated_number,client_id,client_name,callback_url,sub_error"
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterChange, page, sizePerPage]);

    const onSubmit = (data) => {
        setFiltersChange(filterChange + 1);
        setFilters(data);
    };

    const { data, isLoading } = useGenericPostRequestQuery({ url: apiConstants.DELIVERY_DB_SEARCH, data: request, type: 'POST' });

    const conversationOptions = [{ label: "Yes", value: true }, { label: "No", value: false }];

    const columns = [
        {
            field: 'awbNumber',
            headerName: 'AWB No.', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 140, flex: 1,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <CellLine bold>{row?.meta_data?.awbNumber || '—'}</CellLine>
                    <CopyIcon text={row?.meta_data?.awbNumber} />
                </Box>
            ),
        },
        {
            field: 'merchantOid',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => <CellLine>{getMerchantName(row?.meta_data?.merchantOid, merchants) || '—'}</CellLine>,
        },
        {
            field: 'name',
            headerName: 'Customer', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 130, flex: 1,
            renderCell: ({ row }) => <CellLine>{row?.meta_data?.name || '—'}</CellLine>,
        },
        {
            field: 'date',
            headerName: 'Date', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 120, flex: 0.9,
            renderCell: ({ row }) => <CellLine muted>{formatDate(row?.created_date)}</CellLine>,
        },
        {
            field: 'conversationsLength',
            headerName: 'Messages', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 100,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                    {row.conversation_history ? row.conversation_history.length : 0}
                </Box>
            ),
        },
        {
            field: 'conversationsSummary',
            headerName: 'Conversation', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 130,
            renderCell: ({ row }) => (
                <Box
                    component="button"
                    onClick={() => setConversation(row?.conversation_history)}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px',
                        borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                        bgcolor: 'transparent', color: palette.primary.main, border: `1px solid ${palette.primary.lighter}`,
                        transition: 'all 0.15s', '&:hover': { bgcolor: palette.info.lighter, borderColor: palette.info.light },
                    }}
                >
                    View Chat
                </Box>
            ),
        },
    ];

    return (
        <>
            <PageTitle>NDR WhatsApp Automation</PageTitle>
            {isLoading && <Loader />}

            {/* ── KPI Cards ────────────────────────────────────────────── */}
            <WAKPIRow data={data} />

            {/* ── Filter bar ────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awb" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Conversation Completed</FieldLabel>
                                <RHFSelectField
                                    name="isconversationCompleted"
                                    label=""
                                    placeholder="Select Status"
                                    width="100%"
                                    size="small"
                                    displayEmpty
                                    options={conversationOptions.map((val) => ({ value: val.value, label: val.label }))}
                                />
                            </FilterField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Box component="button" type="submit"
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', ...filterButtonSx('apply') }}>
                                    Filter
                                </Box>
                                <Box component="button" type="button"
                                    onClick={() => { reset({ awb: "", merchant: '', isconversationCompleted: '' }); setFilters([]); setFiltersChange(filterChange + 1); }}
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', ...filterButtonSx('reset') }}>
                                    Reset
                                </Box>
                            </Box>

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: palette.background.paper, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={data?.data ? data.data.map((row, index) => ({ ...row, id: index + 1 })) : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    rowCount={Number.MAX_VALUE}
                    hideCountNumber={true}
                    filterMode={"server"}
                    getRowHeight={() => 'auto'}
                    getRowId={(row) => row.id}
                />
            </Box>

            {conversation && <WhatsAppChatPopup conversation={conversation} setConversation={setConversation} />}
        </>
    );
};

export default WhatsAppNDR;
